import React, { useState, useMemo, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import moment from 'moment'
import axios from 'axios'

type Props = {
  show: boolean
  onHide: () => void
  simulation: any
}

const SimulationResultModal: React.FC<Props> = ({ show, onHide, simulation }) => {
  const [expandedDate, setExpandedDate] = useState<string | null>(null)
  const [profileMap, setProfileMap] = useState<any>({})

  const data = useMemo(() => {
    if (!simulation?.result_data) return null
    try {
      const parsedData =
        typeof simulation.result_data === 'string'
          ? JSON.parse(simulation.result_data)
          : simulation.result_data
      return parsedData
    } catch (e) {
      console.error('Failed to parse result_data', e)
      return null
    }
  }, [simulation])
  const simulationSettings = useMemo(() => {
    if (!simulation?.simulation_settings) return {}
    try {
      return typeof simulation.simulation_settings === 'string'
        ? JSON.parse(simulation.simulation_settings)
        : simulation.simulation_settings
    } catch (e) {
      console.error('Failed to parse simulation_settings', e)
      return {}
    }
  }, [simulation])

  const resultDataSource = useMemo(() => {
    if (!simulation?.result_data_source) return {}
    try {
      return typeof simulation.result_data_source === 'string'
        ? JSON.parse(simulation.result_data_source)
        : simulation.result_data_source
    } catch (e) {
      console.error('Failed to parse result_data_source', e)
      return {}
    }
  }, [simulation])

  // Sort dates to ensure chronological order
  const sortedDates = useMemo(() => {
    if (!data) return []
    return Object.keys(data).sort((a, b) => moment(a).unix() - moment(b).unix())
  }, [data])

  // Fetch profile names when simulation changes
  useEffect(() => {
    if (simulation?.company) {
      const params = { company: Number(simulation.company) }
      axios.get('/profiles/getProfilesForSelect', { params }).then((response) => {
        const profiles = response.data.data || []
        const map: any = {}
        profiles.forEach((p: any) => {
          map[p.ID] = {
            name: p.name,
            profile_type: p.profile_type,
            event_type_name: p.event_type_name,
            day: p.day,
          }
        })
        setProfileMap(map)
      })
    }
  }, [simulation?.company])

  // Reset expanded date when data changes
  useEffect(() => {
    if (sortedDates.length > 0) {
      if (!expandedDate || !data[expandedDate]) {
        setExpandedDate(sortedDates[0])
      }
    } else {
      setExpandedDate(null)
    }
  }, [data, sortedDates, expandedDate])

  const getChartOptions = (date: string, isMini: boolean = false): ApexOptions => {
    const dayData = data?.[date]
    const hourlyData = dayData?.hourly_data || {}
    const categories = Object.keys(hourlyData)

    return {
      chart: {
        id: `chart-${date}-${isMini ? 'mini' : 'large'}`,
        toolbar: {
          show: !isMini,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        sparkline: { enabled: isMini },
        animations: { enabled: true },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: isMini ? 2 : 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [20, 100],
        },
      },
      xaxis: {
        categories: categories,
        labels: { show: !isMini },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        show: !isMini,
      },
      tooltip: {
        enabled: !isMini,
        x: { format: 'HH:mm' },
      },
      colors: ['#7239ea', '#50cd89', '#007bff'],
      grid: {
        show: !isMini,
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
      },
      legend: {
        show: !isMini,
        position: 'bottom',
        horizontalAlign: 'center',
      },
    }
  }

  const getChartSeries = (date: string) => {
    const dayData = data?.[date]
    const hourlyData = dayData?.hourly_data || {}
    const lastYearData = dayData?.last_year_hourly_data || {}
    const currentData = dayData?.current_hourly_data?.hourly_data || {}

    const series = [
      {
        name: 'Simulation',
        data: Object.values(hourlyData) as number[],
      },
    ]

    if (Object.keys(lastYearData).length > 0) {
      series.push({
        name: 'Last Year',
        data: Object.values(lastYearData) as number[],
      })
    }

    if (Object.keys(currentData).length > 0) {
      series.push({
        name: 'Current',
        data: Object.values(currentData) as number[],
      })
    }

    return series
  }

  const getProfileInitials = (name: string) => {
    if (!name) return ''
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
  }

  const getProfileBadgeClass = (pid: number) => {
    const colors = ['primary', 'success', 'info', 'warning', 'danger', 'dark', 'secondary']
    const color = colors[pid % colors.length]
    return `badge-light-${color} text-${color}`
  }

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton className='border-0 pb-0'>
        <Modal.Title className='fw-bold fs-2'>Simulation Results</Modal.Title>
      </Modal.Header>
      <Modal.Body className='pt-5'>
        {data ? (
          <div className='simulation-results-container'>
            {/* 7-Slot Calendar View */}
            <div
              className='calendar-wrapper mb-8'
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '10px',
              }}
            >
              {/* Calendar Headers */}
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div
                  key={day}
                  className='text-center fw-bold text-gray-500 fs-7 text-uppercase mb-2'
                >
                  {day}
                </div>
              ))}

              {/* Calendar Slots */}
              {(() => {
                const slots = []
                if (sortedDates.length > 0) {
                  const firstDate = moment(sortedDates[0])
                  let firstDay = firstDate.day() // 0 is Sun
                  // Convert Sun(0) to 7, then 1-7 for Mon-Sun
                  const startOffset = firstDay === 0 ? 6 : firstDay - 1

                  // Add empty slots before the start date
                  for (let i = 0; i < startOffset; i++) {
                    slots.push(<div key={`empty-start-${i}`} className='calendar-slot-empty'></div>)
                  }

                  // Add date slots
                  sortedDates.forEach((date) => {
                    const dayData = data[date]
                    const isActive = expandedDate === date
                    const profilesOnDate = simulationSettings[date]?.profiles || []

                    // Calculate accuracy and background color
                    let bgColor = 'transparent'
                    if (dayData?.current_hourly_data?.total) {
                      const simulation = dayData.total
                      const current = dayData.current_hourly_data.total
                      const accuracy = 100 * (1 - Math.abs(simulation - current) / current)

                      // Color based on accuracy: success (90+), warning (70-89), danger (<70)
                      if (accuracy >= 90) {
                        bgColor = 'rgba(34, 154, 84, 0.1)' // success green
                      } else if (accuracy >= 50) {
                        bgColor = 'rgba(255, 193, 7, 0.1)' // warning yellow
                      } else {
                        bgColor = 'rgba(220, 53, 69, 0.1)' // danger red
                      }
                    }

                    slots.push(
                      <div
                        key={date}
                        onClick={() => setExpandedDate(expandedDate === date ? null : date)}
                        className={`card border shadow-sm cursor-pointer transition-all hover-elevate-up ${
                          isActive ? 'border-primary shadow-lg' : 'border-gray-200'
                        }`}
                        style={{
                          transition: 'all 0.3s ease',
                          minHeight: '140px',
                          transform: isActive ? 'scale(1.02)' : 'scale(1)',
                          zIndex: isActive ? 2 : 1,
                          backgroundColor: isActive ? 'rgba(114, 57, 234, 0.1)' : bgColor,
                        }}
                      >
                        <div className='card-body p-3 d-flex flex-column'>
                          <div className='d-flex justify-content-between align-items-start mb-1'>
                            <div className='fw-bold fs-7 text-gray-800'>
                              {moment(date).format('DD MMM')}
                            </div>
                            <div className='text-primary fw-bold fs-8'>{dayData.total}</div>
                          </div>

                          {/* Profiles */}
                          {simulation?.use_profile_reference === 'yes' &&
                            profilesOnDate.length > 0 && (
                              <div className='d-flex flex-wrap gap-1 mb-1'>
                                {profilesOnDate.slice(0, 2).map((pid: number) => {
                                  const p = profileMap[pid]
                                  if (!p) return null
                                  return (
                                    <span
                                      key={pid}
                                      className={`badge ${getProfileBadgeClass(pid)} p-1`}
                                      style={{ fontSize: '8px' }}
                                    >
                                      {getProfileInitials(p.name)}
                                    </span>
                                  )
                                })}
                                {profilesOnDate.length > 2 && (
                                  <span className='text-muted' style={{ fontSize: '8px' }}>
                                    +{profilesOnDate.length - 2}
                                  </span>
                                )}
                              </div>
                            )}

                          {/* Mini Chart */}
                          <div className='mt-auto' style={{ height: '30px' }}>
                            <Chart
                              options={getChartOptions(date, true)}
                              series={getChartSeries(date)}
                              type='area'
                              height='100%'
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })
                }
                return slots
              })()}
            </div>

            {/* Detailed View - Accordion Style */}
            <div className='details-wrapper' style={{ position: 'relative' }}>
              {expandedDate && data[expandedDate] && (
                <div className='card border-0 shadow-sm bg-light-light animate__animated animate__fadeIn'>
                  <div className='card-header border-0 pt-5 min-h-auto'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                      <h3 className='card-title align-items-start flex-column w-100'>
                        <span className='card-label fw-bold fs-3 mb-4'>
                          {moment(expandedDate).format('dddd, DD MMMM YYYY')}
                        </span>

                        {/* Modern Stats Cards */}
                        <div className='row g-3 mb-4 w-100'>
                          {/* Simulation Total Card */}
                          <div className='col-lg col-md-4 col-sm-6'>
                            <div
                              className='card border-0 h-100'
                              style={{ backgroundColor: 'rgba(114, 57, 234, 0.1)' }}
                            >
                              <div className='card-body p-4'>
                                <div className='d-flex align-items-center mb-2'>
                                  <div className='symbol symbol-40px me-3'>
                                    <span
                                      className='symbol-label'
                                      style={{ backgroundColor: '#7239ea' }}
                                    >
                                      <i className='fa fa-chart-line text-white fs-4'></i>
                                    </span>
                                  </div>
                                  <div className='flex-grow-1'>
                                    <span className='text-gray-600 fw-semibold d-block fs-8'>
                                      Simulation Total
                                    </span>
                                    <span className='fw-bold fs-3' style={{ color: '#7239ea' }}>
                                      {data[expandedDate].total}
                                    </span>
                                    <span className='text-gray-600 fw-semibold fs-7'> kWh</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Current Total Card */}
                          <div className='col-lg col-md-4 col-sm-6'>
                            <div
                              className='card border-0 h-100'
                              style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)' }}
                            >
                              <div className='card-body p-4'>
                                <div className='d-flex align-items-center mb-2'>
                                  <div className='symbol symbol-40px me-3'>
                                    <span
                                      className='symbol-label'
                                      style={{ backgroundColor: '#007bff' }}
                                    >
                                      <i className='fa fa-bolt text-white fs-4'></i>
                                    </span>
                                  </div>
                                  <div className='flex-grow-1'>
                                    <span className='text-gray-600 fw-semibold d-block fs-8'>
                                      Current Total
                                    </span>
                                    <span className='fw-bold fs-3' style={{ color: '#007bff' }}>
                                      {data[expandedDate]?.current_hourly_data?.total ?? 'N/A'}
                                    </span>
                                    {data[expandedDate]?.current_hourly_data?.total && (
                                      <span className='text-gray-600 fw-semibold fs-7'> kWh</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Error Card (kWh difference) */}
                          {data[expandedDate]?.current_hourly_data?.total && (
                            <div className='col-lg col-md-4 col-sm-6'>
                              {(() => {
                                const simulation = data[expandedDate].total
                                const current = data[expandedDate].current_hourly_data.total
                                const errorValue = Math.abs(simulation - current).toFixed(2)
                                return (
                                  <div className='card bg-light-secondary border-0 h-100'>
                                    <div className='card-body p-4'>
                                      <div className='d-flex align-items-center mb-2'>
                                        <div className='symbol symbol-40px me-3'>
                                          <span className='symbol-label bg-secondary'>
                                            <i className='fa fa-minus-circle text-white fs-4'></i>
                                          </span>
                                        </div>
                                        <div className='flex-grow-1'>
                                          <span className='text-gray-600 fw-semibold d-block fs-8'>
                                            Error
                                          </span>
                                          <span className='text-gray-800 fw-bold fs-3'>
                                            {errorValue}
                                          </span>
                                          <span className='text-gray-600 fw-semibold fs-7'>
                                            {' '}
                                            kWh
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          )}

                          {/* Error Ratio Card */}
                          {data[expandedDate]?.current_hourly_data?.total && (
                            <div className='col-lg col-md-4 col-sm-6'>
                              {(() => {
                                const simulation = data[expandedDate].total
                                const current = data[expandedDate].current_hourly_data.total
                                // error ratio (%) = |simulation - current| / current * 100
                                const errorRatio = (
                                  (Math.abs(simulation - current) / current) *
                                  100
                                ).toFixed(2)
                                const errorRatioValue = parseFloat(errorRatio)

                                // 0 = success, 100+ = danger
                                const getColor = () => {
                                  if (errorRatioValue >= 50) return 'danger'
                                  if (errorRatioValue >= 10) return 'warning'
                                  return 'success'
                                }
                                const color = getColor()

                                return (
                                  <div className={`card bg-light-${color} border-0 h-100`}>
                                    <div className='card-body p-4'>
                                      <div className='d-flex align-items-center mb-2'>
                                        <div className='symbol symbol-40px me-3'>
                                          <span className={`symbol-label bg-${color}`}>
                                            <i className='fa fa-exclamation-triangle text-white fs-4'></i>
                                          </span>
                                        </div>
                                        <div className='flex-grow-1'>
                                          <span className='text-gray-600 fw-semibold d-block fs-8'>
                                            Error Ratio
                                          </span>
                                          <span className={`text-${color} fw-bold fs-3`}>
                                            {errorRatio}
                                          </span>
                                          <span className='text-gray-600 fw-semibold fs-7'>%</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          )}

                          {/* Accuracy Card */}
                          {data[expandedDate]?.current_hourly_data?.total && (
                            <div className='col-lg col-md-4 col-sm-6'>
                              {(() => {
                                const simulation = data[expandedDate].total
                                const current = data[expandedDate].current_hourly_data.total
                                // accuracy (%) = 100 × (1 - |simulation - current| / current)
                                const accuracy = (
                                  100 *
                                  (1 - Math.abs(simulation - current) / current)
                                ).toFixed(2)
                                const accuracyValue = parseFloat(accuracy)

                                // 100 = success, 0 = danger
                                const getColor = () => {
                                  if (accuracyValue >= 90) return 'success'
                                  if (accuracyValue >= 50) return 'warning'
                                  return 'danger'
                                }
                                const color = getColor()

                                return (
                                  <div className={`card bg-light-${color} border-0 h-100`}>
                                    <div className='card-body p-4'>
                                      <div className='d-flex align-items-center mb-2'>
                                        <div className='symbol symbol-40px me-3'>
                                          <span className={`symbol-label bg-${color}`}>
                                            <i className='fa fa-check-circle text-white fs-4'></i>
                                          </span>
                                        </div>
                                        <div className='flex-grow-1'>
                                          <span className='text-gray-600 fw-semibold d-block fs-8'>
                                            Accuracy
                                          </span>
                                          <span className={`text-${color} fw-bold fs-3`}>
                                            {accuracy}
                                          </span>
                                          <span className='text-gray-600 fw-semibold fs-7'>%</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                        </div>

                        {/* Profile Badges */}
                        {simulation?.use_profile_reference === 'yes' &&
                          simulationSettings[expandedDate]?.profiles?.length > 0 && (
                            <div className='d-flex align-items-center gap-2 flex-wrap mb-2'>
                              <span className='text-gray-600 fw-bold fs-8'>
                                <i className='fa fa-layer-group me-1'></i>
                                Profiles:
                              </span>
                              {simulationSettings[expandedDate]?.profiles?.map((pid: number) => {
                                const p = profileMap[pid]
                                if (!p) return null
                                return (
                                  <span
                                    key={pid}
                                    className={`badge ${
                                      p.profile_type === 'event_based'
                                        ? 'badge-primary'
                                        : 'badge-success'
                                    }`}
                                    style={{ fontSize: '10px' }}
                                  >
                                    {p.name}{' '}
                                    {p.profile_type === 'event_based'
                                      ? `(${p.event_type_name})`
                                      : `(${p.day})`}
                                  </span>
                                )
                              })}
                            </div>
                          )}
                      </h3>
                    </div>

                    {/* Data Sources */}
                    {resultDataSource[expandedDate] &&
                      resultDataSource[expandedDate].length > 0 && (
                        <div className='mt-4 d-flex flex-wrap gap-2 align-items-center'>
                          <span className='text-muted fw-bold fs-9 text-uppercase'>
                            Data Sources:
                          </span>
                          {resultDataSource[expandedDate].map((source: string, idx: number) => {
                            let displaySource = source
                            if (source.startsWith('Reading: ')) {
                              const datePart = source.replace('Reading: ', '')
                              displaySource = `Reading: ${moment(datePart).format('DD-MM-YYYY')}`
                            }
                            return (
                              <span
                                key={idx}
                                className='badge badge-white border border-gray-300 text-gray-700'
                                style={{ fontSize: '10px' }}
                              >
                                <i className='fa fa-database me-1 fs-10 text-gray-400'></i>
                                {displaySource}
                              </span>
                            )
                          })}
                        </div>
                      )}
                  </div>
                  <div className='card-body py-5'>
                    <Chart
                      options={getChartOptions(expandedDate, false)}
                      series={getChartSeries(expandedDate)}
                      type='area'
                      height={320}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className='text-center py-20'>
            <div className='spinner-border text-primary mb-4' role='status'></div>
            <div className='text-gray-600 fs-5 fw-semibold'>Loading simulation results...</div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='border-0 pt-0'>
        <button className='btn btn-light fw-bold' onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default SimulationResultModal
